import { useState, useRef, useContext } from "react";
import { Modal } from "react-bootstrap";
import { FiUpload } from "react-icons/fi";
import ReactNotifications, { showApiErrors } from "../../../../common/reactNotifications/ReactNotifications";
import * as utilConstants from "../../../../../utils/constants/utilsConstants";
import { getAiParsingData } from "../../../../../api/businessAndEnterprise/sheetApi";
import Context from "../../../../../context/Context";
import { HiSparkles } from "react-icons/hi2";

const AiUploadFeature = ({
    sheet,
    fetchAllCategories,
    fetchAllCompanies,
    fetchAllUnits,
    fetchAllYears,
}) => {
    const [show, setShow] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [addPrompt, setAddPrompt] = useState(false);
    const [prompt, setPrompt] = useState("");
    const fileInputRef = useRef(null);
    const { defaultColumns, setCurrentSheet, setValidSheetData, currentSheet } = useContext(Context);

    const allowedExtensions = [
        "pdf", "csv", "xlsx", "xls",
        "docx", "doc", "pptx", "ppt", "txt"
    ];

    const handleClose = () => {
        setShow(false);
        setSelectedFile(null);
        setErrorMessage("");
        setIsLoading(false);
        setAddPrompt(false);
        setPrompt("");
    };

    const handleShow = () => setShow(true);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateFile(e.dataTransfer.files[0]);
        }
    };

    const validateFile = (file) => {
        const fileExt = file.name.split(".").pop().toLowerCase();

        if (!allowedExtensions.includes(fileExt)) {
            setErrorMessage(
                `Invalid file format. Allowed formats: ${allowedExtensions.join(", ")}`
            );
            setSelectedFile(null);
            return;
        }

        setSelectedFile(file);
        setErrorMessage("");
    };

    const handleInputChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            validateFile(e.target.files[0]);
        }
    };

    const handleButtonClick = () => {
        fileInputRef.current.click();
    };

    const handleUploadAPI = async () => {
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append("file", selectedFile);
        if (addPrompt) {
            formData.append("prompt", prompt);
        }

        setIsLoading(true);

        try {
            getAiParsingData(formData, (sheetResponse) => {
                setIsLoading(false);

                if (sheetResponse?.status) {
                    const existingRows = currentSheet.data.slice(1);

                    sheetResponse?.data && setValidSheetData(sheetResponse?.data);

                    const currentColumns = currentSheet.data[0] || [];
                    const existingYearColumns = currentColumns
                        .slice(defaultColumns.length - 1)
                        .filter((col) => col !== "Time");

                    const newYears = new Set();
                    sheetResponse?.data?.forEach((row) => {
                        row.time_frame_data?.forEach((item) => {
                            if (item.year) {
                                newYears.add(item.year);
                            }
                        });
                    });

                    const allYears = new Set([...existingYearColumns, ...Array.from(newYears)]);
                    const yearColumns = Array.from(allYears).sort();

                    const updatedColumns = [...defaultColumns.slice(0, -1), ...yearColumns];

                    const newRows = sheetResponse?.data?.map((rowData) => {
                        const yearValueMap = {};
                        rowData.time_frame_data?.forEach((item) => {
                            yearValueMap[item.year] = item.value || "";
                        });

                        return [
                            rowData.identifier_code || "",
                            rowData.category_name || "",
                            rowData.category_code || "",
                            rowData.unit_name || "",
                            ...yearColumns.map(year => yearValueMap[year] || "")
                        ];
                    }) || [];

                    const updatedExistingRows = existingRows.map(row => {
                        const existingYearValues = row.slice(defaultColumns.length - 1);
                        const yearValueMap = {};

                        existingYearColumns.forEach((year, index) => {
                            yearValueMap[year] = existingYearValues[index] || "";
                        });

                        return [
                            ...row.slice(0, defaultColumns.length - 1),
                            ...yearColumns.map(year => yearValueMap[year] || "")
                        ];
                    });

                    setCurrentSheet({
                        name: sheet?.title,
                        data: [updatedColumns, ...updatedExistingRows, ...newRows],
                    });
                    fetchAllCategories();
                    fetchAllCompanies();
                    fetchAllUnits();
                    fetchAllYears();
                    ReactNotifications(sheetResponse?.message, utilConstants.sucessState200);
                    handleClose();
                } else {
                    showApiErrors(sheetResponse);
                }
            });

        } catch (error) {
            console.error("Upload failed:", error);
            setIsLoading(false);
            showApiErrors(error);
        }
    };

    return (
        <div>
            <div
                className="relative group flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1 cursor-pointer transition-all duration-200"
                onClick={handleShow}
                onMouseEnter={(e) => {
                    const tooltip = e.currentTarget.querySelector('.lock-tooltip');
                    if (tooltip) tooltip.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                    const tooltip = e.currentTarget.querySelector('.lock-tooltip');
                    if (tooltip) tooltip.style.opacity = '0';
                }}
            >
                <span className="text-yellow-500"><HiSparkles className="w-7 h-7" /></span>
                <div
                    className="lock-tooltip z-[10000] absolute top-full left-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-md whitespace-nowrap pointer-events-none transition-opacity duration-200"
                    style={{
                        transform: 'translateX(-50%)',
                        opacity: 0,
                    }}
                >
                    Upload with AI
                    <div
                        className="absolute bottom-full left-1/2 border-4 border-transparent border-b-gray-900"
                        style={{ transform: 'translateX(-50%)', marginBottom: '-8px' }}
                    ></div>
                </div>
            </div>

            <Modal
                show={show}
                onHide={handleClose}
                centered
                size="md"
                backdrop="static"
                style={{ zIndex: 1050 }}
            >
                <Modal.Header closeButton={!isLoading}>
                    <Modal.Title className="d-flex align-items-center gap-2">
                        <span style={{ color: "#eab308" }}><HiSparkles className="w-7 h-7" /></span>
                        Upload with AI
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body className="p-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center min-h-[300px]">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                                <p className="text-gray-600">Processing your file...</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                className={`border-2 border-dashed rounded-lg p-12 text-center transition-all ${dragActive
                                    ? "border-blue-500 bg-blue-50"
                                    : "border-gray-300"
                                    }`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={handleButtonClick}
                                style={{ cursor: "pointer" }}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".pdf,.csv,.xlsx,.xls,.doc,.docx,.ppt,.pptx,.txt"
                                    onChange={handleInputChange}
                                    style={{ display: "none" }}
                                />

                                <FiUpload size={48} className="text-gray-400 mx-auto mb-4" />

                                {selectedFile ? (
                                    <>
                                        <p className="font-semibold text-black">Selected File:</p>
                                        <p className="text-gray-600 mb-3">{selectedFile.name}</p>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-gray-600">
                                            Click to upload or drag & drop your file
                                        </p>
                                        <p className="text-gray-400 text-sm mt-1">
                                            Supported: pdf, csv, excel, doc, ppt, txt
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="flex items-center mt-3">
                                <input
                                    type="checkbox"
                                    id="addPrompt"
                                    checked={addPrompt}
                                    onChange={(e) => setAddPrompt(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="addPrompt" className="ml-2 block text-sm text-gray-900">
                                    Add a prompt
                                </label>
                            </div>

                            {addPrompt && (
                                <div className="mt-3">
                                    <textarea
                                        value={prompt}
                                        onChange={(e) => setPrompt(e.target.value)}
                                        placeholder="Enter your prompt here..."
                                        className="w-full h-24 p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>
                            )}

                            {errorMessage && (
                                <p className="text-red-600 text-sm mt-3 text-center animate-fadeIn">
                                    {errorMessage}
                                </p>
                            )}

                            {selectedFile && (
                                <div className="mt-4 text-end">
                                    <button
                                        className="bg-[#AEF468] text-black px-6 py-2 rounded-lg border-0"
                                        onClick={handleUploadAPI}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Uploading..." : "Upload File"}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AiUploadFeature;